import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useSignIn, useSignUp } from "@clerk/clerk-react";
import { RefreshCw } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import BackButton from "../components/BackButton";
import OtpSuccess from "../components/OtpSuccess";
import { Button, useIsMobile } from "../../../components/ui";
import {
  verifyLoginOtp,
  verifySignupOtp,
  resendLoginOtp,
  resendSignupOtp,
} from "../services/authService";
import { ApiError } from "../../../lib/http";
import { ROUTES } from "../../../routes/paths";
import { traceRedirect } from "../../../routes/routeTrace";
import type { AuthFlow, OtpVerificationState } from "../types/auth.types";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 45;

const VerifyOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as OtpVerificationState | null;

  // Clerk keeps the attempt started on the previous screen on its client
  // object, so these hooks hand back the same one - nothing about it needs
  // carrying through router state. `setActive` is the same function on both.
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp } = useSignUp();
  const clerkLoaded = signInLoaded && signUpLoaded;

  /**
   * The attempt Clerk is holding, if there is one. `status` is null until
   * something has actually been started, which is what tells a live attempt
   * apart from the empty resource the hooks always hand back.
   */
  const pendingSignUp = signUp?.status ? signUp : null;
  const pendingSignIn = signIn?.status ? signIn : null;

  /**
   * Who this screen is for, from the router state where there is any and from
   * Clerk's own attempt where there is not.
   *
   * The state is the better source - it is what the previous screen meant - but
   * it is not a reliable one: it lives in the history entry, and anything that
   * replaces the entry loses it. Clerk finishing a sign-up with a top-level
   * redirect does that, and so does the Google route, which arrives here from
   * the callback screen with no state at all. The attempt itself survives all
   * of it on Clerk's client, so it is what the screen falls back to rather than
   * throwing the user back to sign up with a half-created account behind them.
   */
  // Only a live sign-in reads as a log-in. With neither attempt in flight
  // nothing is being verified at all, and that case belongs to the guard at
  // the foot of the screen - which sends it to sign up, as it always has.
  const flow: AuthFlow =
    state?.flow ?? (pendingSignIn && !pendingSignUp ? "login" : "signup");
  const isLogin = flow === "login";
  const email =
    state?.email ??
    (isLogin ? pendingSignIn?.identifier : pendingSignUp?.emailAddress) ??
    "";
  const originRoute = isLogin ? ROUTES.login : ROUTES.signup;

  // Which address a log-in resend goes to. Recovered from the factor Clerk
  // prepared when the state that carried it is gone, for the same reason.
  const emailAddressId =
    state?.emailAddressId ??
    pendingSignIn?.supportedFirstFactors?.find(
      (factor): factor is typeof factor & { emailAddressId: string } =>
        factor.strategy === "email_code"
    )?.emailAddressId;

  // Whether the confirmation screen is worth showing, which is a question only
  // a viewport can answer - so it is this hook rather than a `sm:` class. See
  // `OtpSuccess` for why a desktop does not get one.
  const isMobile = useIsMobile();

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  /** The code was accepted and the confirmation screen has the floor. It
   *  navigates when its own hold is up; nothing here does. */
  const [verified, setVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (email) {
      inputRefs.current[0]?.focus();
    }
  }, [email]);

  /**
   * Sends the code for a sign-up that arrived here without one.
   *
   * Every route that navigates here on purpose has already asked Clerk to send
   * it, and `status` says so - null before the address has been prepared for
   * verification, set from then on. It is only null here when Clerk sent the
   * user to this screen itself, which is what it does with a Google sign-up
   * whose email it still wants verified. Without this the screen would ask for
   * a code that was never sent.
   */
  const codeRequested = useRef(false);
  useEffect(() => {
    if (!clerkLoaded || isLogin || !pendingSignUp) return;
    if (pendingSignUp.verifications.emailAddress.status) return;
    // StrictMode runs effects twice in development, and this one sends mail.
    if (codeRequested.current) return;
    codeRequested.current = true;

    resendSignupOtp(pendingSignUp).catch((err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not send the code. Please try again."
      );
      console.error("Could not send the sign-up code:", err);
    });
  }, [clerkLoaded, isLogin, pendingSignUp]);

  // Resend cooldown. Stops at 0 and stays there until a resend restarts it.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const maskEmail = (value: string): string => {
    const [localPart, domain] = value.split("@");
    if (!domain || localPart.length <= 3) {
      return value;
    }
    const asterisks = "*".repeat(localPart.length - 3);
    return `${localPart.substring(0, 3)}${asterisks}@${domain}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleVerify = useCallback(async () => {
    if (!otp.every((digit) => digit !== "")) return;
    if (!clerkLoaded || !signIn || !signUp || !setActive) return;

    setIsVerifying(true);
    setError("");

    try {
      const code = otp.join("");
      const sessionId = isLogin
        ? await verifyLoginOtp(signIn, code)
        : await verifySignupOtp(signUp, code);

      // Activating the session is what actually signs the user in - until
      // this resolves, `useAuth().isSignedIn` is still false and the route
      // guard would bounce us straight back here.
      await setActive({ session: sessionId });

      // Our own backend is introduced to the session by `RequireAuth` on the
      // way in, not from here - it holds its loading screen until whoami and
      // the workspace call have answered, so whatever is behind this
      // navigation does not render until then. See `useSessionBootstrap`.
      //
      // The trial either way, and not only for a brand new account. It is the
      // screen that *asks* - `can_start_trial` off `GET /billing/subscription/`
      // - and it sends anyone who cannot take one straight on to the dashboard.
      // Landing on the dashboard and being pulled back would put `/dashboard`
      // in the address bar for somebody who is about to be shown something
      // else, which is a URL that was never true.
      // A phone is told the code was right before it is shown the next form;
      // `OtpSuccess` navigates when it is done. `isVerifying` deliberately
      // stays set - the boxes and the button underneath the confirmation must
      // not come back to life behind it.
      if (isMobile) {
        setVerified(true);
        return;
      }

      navigate(ROUTES.trial, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Verification error. Please try again.");
      }
      setIsVerifying(false);
      inputRefs.current[0]?.focus();
      console.error("OTP Verification Error:", err);
    }
  }, [otp, isLogin, clerkLoaded, signIn, signUp, setActive, navigate, isMobile]);

  /**
   * Submits the code as soon as there is a whole one, rather than waiting to be
   * asked.
   *
   * What a phone does with a code it was sent: the user typed the sixth digit
   * because the sixth digit was the last thing to do, and a button underneath
   * asking them to confirm that is a tap that carries no decision. It matters
   * more than it sounds on the route this screen is usually reached by - the
   * code arrives in a notification, the keyboard fills the screen, and the
   * button is behind it.
   *
   * Keyed on the code rather than on "is it complete", so a rejected code is
   * attempted once and then left alone. Without that the failed state - six
   * digits present, not verifying - is indistinguishable from the state that
   * fires this, and the screen would submit the same wrong code forever.
   * Changing any digit produces a code that has not been tried, which is what
   * arms it again.
   *
   * The Verify button stays: it is what carries the spinner, it is the way back
   * in after a failure that left the digits alone, and a form a keyboard can
   * submit needs something to submit it.
   */
  const code = otp.join("");
  const attempted = useRef("");
  useEffect(() => {
    if (code.length < OTP_LENGTH || attempted.current === code) return;
    attempted.current = code;
    handleVerify();
  }, [code, handleVerify]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError("");

      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === "Enter" && isOtpComplete) {
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!digits) return;

    const newOtp = Array.from(
      { length: OTP_LENGTH },
      (_, i) => digits[i] || ""
    );
    setOtp(newOtp);
    setError("");

    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    inputRefs.current[
      nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex
    ]?.focus();
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || isResending || isVerifying) return;
    if (!clerkLoaded || !signIn || !signUp) return;

    setIsResending(true);
    setError("");
    try {
      if (isLogin) {
        await resendLoginOtp(signIn, emailAddressId ?? "");
      } else {
        await resendSignupOtp(signUp);
      }
      setOtp(Array(OTP_LENGTH).fill(""));
      // The boxes are empty again, but the code that was tried has to be
      // forgotten too: a user who retypes the same six digits against a freshly
      // sent code is making a new attempt, not repeating a dead one.
      attempted.current = "";
      setSecondsLeft(RESEND_SECONDS);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not resend the code. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  // Nothing can be decided until Clerk has answered, because a screen reached
  // without router state has only Clerk's attempt to go on - and until it
  // loads, an attempt that exists is indistinguishable from none at all.
  // Redirecting here is what sent a perfectly good sign-up back to the form.
  if (!clerkLoaded && !state?.email) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center py-10">
          <span
            role="status"
            aria-label="Loading"
            className="h-8 w-8 rounded-full border-2 border-[#DCE0E4] border-t-[#00C2CB] animate-spin"
          />
        </div>
      </AuthLayout>
    );
  }

  // Guard: neither the previous screen nor Clerk knows of an attempt, so there
  // is nothing on this screen to verify - a direct visit, or one whose attempt
  // has since been abandoned.
  if (!email) {
    traceRedirect(ROUTES.verifyOtp, originRoute, "no attempt to verify");
    return <Navigate to={originRoute} replace />;
  }

  return (
    <AuthLayout phoneFill>
      <BackButton className="mb-5" />

      {/* Sized against the column it heads, which is the width of the glass on
          a phone and 460px on a desktop - the same reasoning as the log-in
          screen's heading. */}
      <h1 className="text-[30px] leading-[1.15] font-medium text-[#1E1F24] sm:text-[26px] sm:leading-tight">
        Verify your account
      </h1>
      <p className="mt-2 text-[15px] text-[#62636C] sm:mt-1 sm:text-[14px]">
        We sent a verification code to email{" "}
        {/* Its own line on a phone. Inline, it wraps wherever the address
            happens to break the sentence, which puts a stray "email" at the
            end of one line and the address alone on the next anyway - so it is
            given the line deliberately rather than by accident. Inline again
            from sm up, where the sentence fits. */}
        <span className="block sm:inline sm:ml-1 font-medium text-[#1E1F24]">
          {maskEmail(email)}
        </span>
      </p>

      {/* Six boxes must fit a 320px-wide viewport, so they take whatever the
          gaps leave rather than a width of their own: the row is the full
          column and `aspect-square` makes each box as tall as it ends up wide.
          From sm up they are the designed 52px square.

          The separator is a desktop thing. It is there to break six digits into
          two threes for an eye scanning a wide row, and on a phone it is a
          seventh cell's worth of width taken out of six boxes that were already
          the narrowest thing on the screen. No phone keypad shows one. */}
      <div className="mt-8 flex items-center gap-2 sm:gap-3">
        {otp.map((digit, index) => (
          <React.Fragment key={index}>
            {index === OTP_LENGTH / 2 && (
              <span
                className="hidden sm:inline-block sm:w-4 text-center text-[#C4CBD1]"
                aria-hidden
              >
                -
              </span>
            )}
            <input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              aria-label={`Digit ${index + 1}`}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              disabled={isVerifying}
              // Three states a phone can tell apart at a glance, because on
              // this screen the row is the only thing there is and how far
              // through the code the user is has to read from under the thumb
              // covering one of them: waiting is a filled grey slot, filled is
              // white with a teal edge and a shadow under it, and wrong is the
              // whole row in red. The lift on a filled box is what makes the
              // row read as six things being picked up one at a time rather
              // than as six boxes with characters in them.
              //
              // All of it is undone at `sm:`, back to the designed white box
              // on a hairline border.
              className={`w-full min-w-0 aspect-square rounded-xl text-[20px] sm:w-[52px] sm:h-[52px] sm:aspect-auto sm:rounded-lg sm:text-lg sm:bg-white sm:shadow-none sm:border-[#DCE0E4] sm:focus:ring-0 text-center font-medium border focus:outline-none focus:bg-white focus:border-[#00C2CB] focus:ring-4 focus:ring-[#00C2CB]/15 transition-all ${
                error
                  ? "border-red-500 bg-[#FEF6F6]"
                  : digit
                  ? "border-[#00C2CB] bg-white shadow-[0_4px_12px_-4px_rgba(0,194,203,0.45)]"
                  : "border-[#E6EBEC] bg-[#F4F7F8]"
              } ${isVerifying ? "opacity-50 cursor-not-allowed" : ""}`}
            />
          </React.Fragment>
        ))}
      </div>

      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}

      <Button
        variant="primary"
        size="touch"
        fullWidth
        onClick={handleVerify}
        disabled={!isOtpComplete || isVerifying || !clerkLoaded}
        // The lift and the disabled hairline, both for the reasons the log-in
        // submit gives.
        className="mt-5 enabled:shadow-[0_10px_24px_-12px_rgba(0,194,203,0.9)] disabled:border disabled:border-[#B7EDF3] sm:disabled:border-0 sm:shadow-none"
      >
        {isVerifying ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          "Verify"
        )}
      </Button>

      {/* Pinned to the foot of a phone screen for the reason the log-in screen
          gives: the resend is the way off this screen, and the Verify button
          above belongs with the boxes it submits. */}
      <div className="flex-1 sm:hidden" aria-hidden />

      <p className="mt-6 sm:mt-8 text-center text-[14px] text-[#3A4652]">
        Didn&rsquo;t receive OTP?{" "}
        {secondsLeft > 0 ? (
          <>
            Resend <span className="font-semibold text-[#00C2CB]">{secondsLeft}s</span>
          </>
        ) : (
          // Padded out to a thumb and pulled back in by the same amount, so
          // the line reads at the spacing it was drawn at.
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || isVerifying}
            className="-m-2 p-2 font-semibold text-[#00C2CB] hover:underline disabled:opacity-50"
          >
            {isResending ? "Sending..." : "Resend"}
          </button>
        )}
      </p>

      {/* Over everything, including the layout's own scroll container - it is
          `fixed`, so where it sits in this tree does not matter. It is the last
          child only so that reading the file top to bottom reaches it where it
          happens: after the screen it takes over from. */}
      {verified && (
        <OtpSuccess
          email={maskEmail(email)}
          onDone={() => navigate(ROUTES.trial, { replace: true })}
        />
      )}
    </AuthLayout>
  );
};

export default VerifyOtpPage;
